/**
 * AppCoordinator - Manages application-level state and configuration
 *
 * Responsibilities:
 * - Calculate and cache static host properties (isCloudHost, guestAccount, etc.)
 * - Provide host geolocation (geohash, coords)
 * - Single source of truth for host metadata
 *
 * Future: Will expand to include host settings/preferences
 */

import * as Sentry from '@sentry/react';
import { getHostGeohash } from '../../utilities/geolocation';
import { makeStateStore } from '@playcastdotio/StateStore';
import type { PlaycastHostEdition, PlaycastHostOneGamepadState, StateStore } from '@playcastdotio/CoreTypes';
import { CoordinatorEventImpl } from '../_types/events';
import type { IHostAnalyticsManager } from '../../services/analytics/IHostAnalyticsManager';

/**
 * Static Host Properties
 *
 * These values are calculated once on init and rarely change.
 * Other coordinators (Session, Pavilion) read from here for consistency.
 */
export type StaticHostProperties = {
  isCloudHost: boolean;
  guestAccount: boolean;
  geohash: string | undefined;
  coords: { lat: number; lng: number } | undefined;
};

/**
 * AppCoordinator
 *
 * Application-level coordinator that manages host metadata and configuration.
 */
/**
 * App-level state replicated to the node-service on the hostState channel.
 * Merged with broadcast status from SessionCoordinator on the node-service side.
 */
type AppHostState = {
  edition: PlaycastHostEdition;
};

export type AuthState = {
  isAuthenticated: boolean;
  accessToken: string;
};

const RESYNC_CHECK_DELAYS_MS = [1500, 3000, 5000, 8000];
export const OPTIONAL_LOCATION_STARTUP_BUDGET_MS = 500;

export class AppCoordinator {
  private hostAuthId = '';
  private hostGamepads: PlaycastHostOneGamepadState[] = [];
  private authState: AuthState = { isAuthenticated: false, accessToken: '' };
  private authListeners: ((state: AuthState) => void)[] = [];
  private hasSynced = false;
  private resyncAttempt = 0;

  public events = {
    hostGamepadsChanged: new CoordinatorEventImpl<PlaycastHostOneGamepadState[]>(),
  };

  private staticProperties: StaticHostProperties = {
    isCloudHost: false,
    guestAccount: false,
    geohash: undefined,
    coords: undefined,
  };

  private analytics: IHostAnalyticsManager;

  /** Replicated to node-service so it knows the host edition for guest mode decisions. */
  private hostStateStore = makeStateStore<AppHostState>({
    edition: 'pavilion',
  }, {
    replicate: { channel: 'hostState', targets: ['nodeService'] },
  });

  constructor(analytics: IHostAnalyticsManager) {
    this.analytics = analytics;
  }

  /**
   * Initialize the coordinator
   *
   * Calculates static host properties once at app startup.
   */
  public async init(): Promise<void> {
    console.log('[AppCoordinator] Initializing...');

    // Calculate static properties
    await this.calculateStaticProperties();

    console.log('[AppCoordinator] ✓ Initialized with static properties:', this.staticProperties);
  }

  /**
   * Calculate static host properties (once on init)
   */
  private async calculateStaticProperties(): Promise<void> {
    console.log('[AppCoordinator] Calculating static properties...');

    // Guest account (read from global window object)
    const guestAccount = !!window.playcast?.guestAccount;

    // Cloud host detection
    // TODO: Remove hardcode when ready for production
    const isCloudHost = this.detectCloudHost();

    this.staticProperties = {
      isCloudHost,
      guestAccount,
      geohash: undefined,
      coords: undefined,
    };
    // Optional matchmaking metadata must not hold the local editor/native transport hostage.
    // Keep the lookup/cache behavior, but let startup continue if a service body stalls.
    const location = this.calculateGeolocation().then(value => { this.staticProperties = { ...this.staticProperties, ...value }; });
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([location, new Promise<void>(resolve => { timer = setTimeout(resolve, OPTIONAL_LOCATION_STARTUP_BUDGET_MS); })]);
    } finally { clearTimeout(timer); }
    console.log('[AppCoordinator] ✓ Static properties calculated');
  }

  /**
   * Detect if this host is running in a cloud environment.
   *
   * Cloud host status is determined server-side during authentication
   * (via cloudHostToken validation). This returns false initially;
   * the actual value is set when AUTH_SUCCESS is received.
   */
  private detectCloudHost(): boolean {
    return false;
  }

  /**
   * Update cloud host status after server confirmation.
   * Called when AUTH_SUCCESS includes isCloudHost: true.
   */
  public updateCloudHostStatus(isCloudHost: boolean): void {
    if (isCloudHost !== this.staticProperties.isCloudHost) {
      console.log('[AppCoordinator] Cloud host status updated:', isCloudHost);
      this.staticProperties = { ...this.staticProperties, isCloudHost };
    }
  }

  /**
   * Calculate host geolocation
   */
  private async calculateGeolocation(): Promise<{ geohash: string | undefined; coords: { lat: number; lng: number } | undefined }> {
    try {
      const result = await getHostGeohash();

      if (result.hash) {
        console.log('[AppCoordinator] ✓ Geolocation obtained:', result.hash);
        return {
          geohash: result.hash,
          coords: result.coords,
        };
      }

      console.warn('[AppCoordinator] ⚠️ Geolocation unavailable (fallback)');
      return { geohash: undefined, coords: undefined };
    } catch (error) {
      console.error('[AppCoordinator] ❌ Geolocation failed:', error);
      // Return undefined if geolocation fails - host can still operate without it
      return { geohash: undefined, coords: undefined };
    }
  }

  // ========================================
  // Public API - Getters (READ)
  // ========================================

  /**
   * Get all static host properties
   */
  public getStaticProperties(): StaticHostProperties {
    return { ...this.staticProperties };
  }

  /**
   * Get cloud host status
   */
  public getIsCloudHost(): boolean {
    return this.staticProperties.isCloudHost;
  }

  /**
   * Get guest account status
   */
  public getGuestAccount(): boolean {
    return this.staticProperties.guestAccount;
  }

  /**
   * Get host geohash
   */
  public getGeohash(): string | undefined {
    return this.staticProperties.geohash;
  }

  /**
   * Get host coordinates
   */
  public getCoords(): { lat: number; lng: number } | undefined {
    return this.staticProperties.coords;
  }

  /**
   * Get host edition
   */
  public getEdition(): PlaycastHostEdition {
    return this.hostStateStore.currentState.edition;
  }

  // ========================================
  // Public API - Setters (WRITE)
  // ========================================

  /**
   * Get host auth ID (bridged from legacy authState via init.ts)
   */
  public getHostAuthId(): string {
    return this.hostAuthId;
  }

  /**
   * Update host auth ID (called from init.ts authState bridge)
   */
  public updateHostAuthId(id: string): void {
    this.hostAuthId = id;
  }

  public getAuthState(): AuthState {
    return this.authState;
  }

  public updateAuthState(accessToken: string): void {
    const isAuthenticated = !!accessToken && !accessToken.startsWith('hpl;');
    const next = { isAuthenticated, accessToken };
    this.authState = next;
    this.authListeners.forEach((cb) => cb(next));
  }

  public addAuthListener(cb: (state: AuthState) => void): number {
    this.authListeners.push(cb);
    return this.authListeners.length - 1;
  }

  public removeAuthListener(id: number): void {
    this.authListeners.splice(id, 1);
  }

  // ========================================
  // Public API - Host Gamepads
  // ========================================

  /**
   * Get currently connected physical host gamepads.
   */
  public getHostGamepads(): PlaycastHostOneGamepadState[] {
    return this.hostGamepads;
  }

  /**
   * Register an IPC gamepad state store to track host hardware gamepads.
   * Filters to physical gamepads only and emits hostGamepadsChanged on updates.
   */
  public registerHostGamepadStore(store: StateStore<{ gamepads: PlaycastHostOneGamepadState[] }>): void {
    this.updateHostGamepadsFromStore(store.currentState.gamepads);
    store.addListener((state) => {
      this.updateHostGamepadsFromStore(state.gamepads);
    });
  }

  private updateHostGamepadsFromStore(gamepads: PlaycastHostOneGamepadState[]): void {
    const physical = gamepads.filter((g) => g.isPhysical);
    this.hostGamepads = physical;
    this.events.hostGamepadsChanged.emit(physical);
  }

  // ========================================
  // Replication sync safety
  // ========================================

  public getHasSynced(): boolean {
    return this.hasSynced;
  }

  /**
   * Called by the IPC handler when the first replication message
   * arrives from Electron, proving the snapshot was received.
   */
  public markSynced(): void {
    if (this.hasSynced) return;
    this.hasSynced = true;
    console.log('[AppCoordinator] Electron replication received, hasSynced = true');
  }

  /**
   * Schedule a series of resync checks with increasing delays.
   * Each check requests Electron to re-send the full state snapshot
   * if no replication has been received yet.
   */
  public scheduleResyncCheck(): void {
    this.resyncAttempt = 0;
    this.scheduleNextResyncAttempt();
  }

  private scheduleNextResyncAttempt(): void {
    if (this.resyncAttempt >= RESYNC_CHECK_DELAYS_MS.length) return;

    const delayMs = RESYNC_CHECK_DELAYS_MS[this.resyncAttempt];

    setTimeout(() => {
      if (this.hasSynced) return;

      this.resyncAttempt++;
      const isLastAttempt = this.resyncAttempt >= RESYNC_CHECK_DELAYS_MS.length;

      console.warn(
        `[AppCoordinator] hasSynced still false after ${delayMs}ms (attempt ${this.resyncAttempt}/${RESYNC_CHECK_DELAYS_MS.length}), requesting resync`
      );

      Sentry.captureMessage(
        `[Resync] Frontend requesting resync (attempt ${this.resyncAttempt}/${RESYNC_CHECK_DELAYS_MS.length})`,
        {
          level: isLastAttempt ? 'error' : 'warning',
          tags: {
            issue_type: 'replication_resync',
            resync_attempt: String(this.resyncAttempt),
            is_last_attempt: String(isLastAttempt),
          },
        },
      );

      window.playcast?.requestResync?.();
      this.scheduleNextResyncAttempt();
    }, delayMs);
  }
}

// Production singleton: `export const appCoordinator` in coordinators/init.ts
